/**
 * Search – Predictive Search
 * The Color – thecolor.uk
 *
 * Handles:
 *  - Predictive search overlay with debounced input
 *  - Fetches results from Shopify's predictive search API (per-type limits)
 *  - Intent rows (Christmas gifts, stocking fillers, vouchers, tools) and
 *    size shortcuts (1 Litre, 500 ml, travel) — same terms as /search
 *  - Empty-overlay routes: Popular + Popular searches; zero results add the
 *    salon phone number
 *  - Keyboard navigation through results (ArrowDown/ArrowUp)
 *  - Focus trap + TheColor.ScrollLock while open, and the visual viewport
 *    height (--tc-vvh) so results stay above the iPhone keyboard
 *  - A one-line role=status summary instead of a live results list
 */

(function () {
  'use strict';

  const DEBOUNCE_MS = 250;
  /* Two letters already narrow this catalogue well ("tt", "f&" …); three
     left short queries with nothing but the default chips. */
  const MIN_CHARS = 2;
  const LOCK_OWNER = 'search';

  /* ALL toggles (2026-08-20): the header has a desktop search bar, a laptop
     icon and the phone strip, all [data-search-toggle]. */
  const searchToggles = Array.from(document.querySelectorAll('[data-search-toggle]'));
  let lastToggle = null;
  const searchOverlay = document.getElementById('search-overlay');
  const searchInput = searchOverlay?.querySelector('[data-search-input]');
  const searchResults = searchOverlay?.querySelector('[data-search-results]');
  const searchStatus = searchOverlay?.querySelector('[data-search-status]');
  const searchClose = searchOverlay?.querySelector('[data-search-close]');
  const header = searchOverlay ? searchOverlay.closest('.tc-header') : null;

  let debounceTimer = null;
  let abortController = null;
  let releaseFocus = null;
  let statusTimer = null;

  /* -----------------------------------------------------------------------
     0. Helpers
     ----------------------------------------------------------------------- */

  function TC() {
    return window.TheColor || {};
  }

  function lockScroll() {
    const SL = TC().ScrollLock;
    if (SL && typeof SL.lock === 'function') {
      SL.lock(LOCK_OWNER);
    } else {
      document.documentElement.style.overflow = 'hidden';
    }
  }

  function unlockScroll() {
    const SL = TC().ScrollLock;
    if (SL && typeof SL.unlock === 'function') {
      SL.unlock(LOCK_OWNER);
    } else {
      document.documentElement.style.overflow = '';
    }
  }

  /* The keyboard on iOS shrinks only the visual viewport (dvh stays put), so
     the results' max-height follows visualViewport.height while open. */
  function syncViewport() {
    if (!searchOverlay || !window.visualViewport) return;
    searchOverlay.style.setProperty('--tc-vvh', `${Math.round(window.visualViewport.height)}px`);
  }

  function bindViewport(on) {
    const vv = window.visualViewport;
    if (!vv) return;
    const method = on ? 'addEventListener' : 'removeEventListener';
    vv[method]('resize', syncViewport);
    vv[method]('scroll', syncViewport);
    if (on) syncViewport();
  }

  function setStatus(text) {
    if (!searchStatus) return;
    clearTimeout(statusTimer);
    // Clear first so the same sentence twice is still announced.
    searchStatus.textContent = '';
    statusTimer = setTimeout(() => {
      searchStatus.textContent = text;
    }, 120);
  }

  function plural(n, one, many) {
    return `${n} ${n === 1 ? one : many}`;
  }

  /* Visible titles drop the leading brand ("Paul Mitchell Tea Tree Special
     Shampoo" → "Tea Tree Special Shampoo"); screen readers still hear it. */
  function titleHtml(title) {
    const t = String(title || '');
    const m = t.match(/^(Paul Mitchell)\s+(.+)$/i);
    if (!m) return escapeHTML(t);
    return `<span class="tc-sr-only">${escapeHTML(m[1])} </span>${escapeHTML(m[2])}`;
  }

  /* -----------------------------------------------------------------------
     1. Toggle Overlay
     ----------------------------------------------------------------------- */

  function isOpen() {
    return !!searchOverlay && !searchOverlay.hasAttribute('hidden');
  }

  function openSearch() {
    if (!searchOverlay || isOpen()) return;

    searchOverlay.removeAttribute('hidden');
    searchOverlay.setAttribute('aria-hidden', 'false');
    if (header) header.classList.add('is-search-open');
    searchToggles.forEach((t) => t.setAttribute('aria-expanded', 'true'));
    lockScroll();
    bindViewport(true);

    // Trap focus inside overlay (the input gets focus below)
    if (TC().trapFocus) {
      releaseFocus = TC().trapFocus(searchOverlay, { focusFirst: false });
    }

    searchInput?.focus({ preventScroll: true });
    requestAnimationFrame(() => {
      if (document.activeElement !== searchInput) searchInput?.focus({ preventScroll: true });
    });

    if (searchInput && !searchInput.value.trim()) renderDefaultState();
  }

  function closeSearch() {
    if (!searchOverlay || !isOpen()) return;

    searchOverlay.setAttribute('hidden', '');
    searchOverlay.setAttribute('aria-hidden', 'true');
    if (header) header.classList.remove('is-search-open');
    searchToggles.forEach((t) => t.setAttribute('aria-expanded', 'false'));
    bindViewport(false);
    clearTimeout(debounceTimer);
    if (abortController) abortController.abort();

    // Release focus trap
    if (releaseFocus) {
      releaseFocus();
      releaseFocus = null;
    }
    unlockScroll();

    // Clear results
    if (searchResults) searchResults.innerHTML = '';
    if (searchStatus) searchStatus.textContent = '';
    if (searchInput) searchInput.value = '';

    (lastToggle || searchToggles[0])?.focus({ preventScroll: true });
  }

  searchToggles.forEach((toggle) => toggle.addEventListener('click', (e) => {
    lastToggle = toggle;
    e.preventDefault();
    openSearch();
  }));

  searchClose?.addEventListener('click', closeSearch);

  // Close on Escape
  searchOverlay?.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      closeSearch();
      return;
    }

    // Arrow key navigation through results
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      const links = searchResults
        ? Array.from(searchResults.querySelectorAll('.tc-search-predict__link, .tc-search-predict__intent[href]'))
        : [];
      if (!links.length) return;

      e.preventDefault();
      const currentEl = document.activeElement;
      const currentIdx = links.indexOf(currentEl);

      if (e.key === 'ArrowDown') {
        if (currentEl === searchInput || currentIdx === -1) {
          links[0].focus();
        } else if (currentIdx < links.length - 1) {
          links[currentIdx + 1].focus();
        }
      } else if (currentIdx <= 0) {
        searchInput?.focus();
      } else {
        links[currentIdx - 1].focus();
      }
    }
  });

  // Escape also works when focus has dropped to <body> (a focused result row
  // was replaced by a fresh render): the listener above only hears keys
  // pressed inside the overlay.
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape' || !isOpen()) return;
    if (searchOverlay.contains(e.target)) return; // handled above
    closeSearch();
  });

  // Close on backdrop click
  searchOverlay?.addEventListener('click', (e) => {
    if (e.target === searchOverlay) closeSearch();
  });

  /* -----------------------------------------------------------------------
     2. Intents and size shortcuts (mirror sections/search-results.liquid)
     ----------------------------------------------------------------------- */

  function normalise(q) {
    return String(q || '')
      .toLowerCase()
      .replace(/&/g, ' and ')
      .replace(/[’']/g, '')
      .replace(/[-,.?!]/g, ' ')
      .replace(/(\d)\s+(ml|l)\b/g, '$1$2')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function salonPhone() {
    if (!searchOverlay) return null;
    const label = searchOverlay.getAttribute('data-salon-phone') || '';
    const link = searchOverlay.getAttribute('data-salon-phone-link') || '';
    return label && link ? { label, link } : null;
  }

  /* Exact phrases → one key page. */
  const INTENTS = [
    {
      re: /^(christmas|xmas|christmas gifts?|xmas gifts?|christmas presents?|secret santa|presents?|gifts? for (her|him|mum|dad|men|women|a man|a woman))$/,
      href: '/collections/gift-sets-duos',
      html: 'Christmas gifts? Shop <strong>Gift Sets</strong>',
    },
    {
      re: /^stocking (fillers?|stuffers?)$/,
      href: '/collections/under-15',
      html: 'Stocking fillers: shop <strong>Under £15</strong>',
    },
    {
      re: /^(tools?|brush|brushes|hair ?brush(es)?|combs?|hair tools?|styling tools?|tools and brushes)$/,
      href: '/collections/tools',
      html: 'Shop <strong>Tools &amp; Brushes</strong>',
    },
    { re: /^(gift ?cards?|gift ?vouchers?|vouchers?|salon (gift )?vouchers?|gift certificates?)$/, voucher: true },
  ];

  /* A size anywhere in the query → that size's collection. */
  const SIZES = [
    { re: /\b(1l|1 litre|1 liter|litres?|liters?|1000ml|33\.8 ?oz)\b/, href: '/collections/paul-mitchell-1-litre', html: 'Shop all <strong>1 Litre salon sizes</strong>' },
    { re: /\b500ml\b/, href: '/collections/paul-mitchell-500ml', html: 'Shop all <strong>500 ml sizes</strong>' },
    { re: /\b(travel|travel size|minis?|75ml)\b/, href: '/collections/travel-size', html: 'Shop all <strong>travel sizes &amp; minis</strong>' },
  ];

  function intentRow(query, hasCollections) {
    const q = normalise(query);
    for (let i = 0; i < INTENTS.length; i += 1) {
      const it = INTENTS[i];
      if (!it.re.test(q)) continue;
      if (it.voucher) {
        const phone = salonPhone();
        const call = phone
          ? `call <a href="tel:${escapeAttr(phone.link)}">${escapeHTML(phone.label)}</a> or `
          : '';
        return `<p class="tc-search-predict__intent">
            <span>Salon gift vouchers are available — ${call}<a href="/pages/contact?subject=Gift%20voucher">send us a message</a>.</span>
          </p>`;
      }
      return arrowRow(it.href, it.html);
    }
    if (hasCollections) return '';
    for (let i = 0; i < SIZES.length; i += 1) {
      if (SIZES[i].re.test(q)) return arrowRow(SIZES[i].href, SIZES[i].html);
    }
    return '';
  }

  function arrowRow(href, html) {
    return `<a class="tc-search-predict__intent" href="${escapeAttr(href)}">
        <span>${html}</span>
        <span class="tc-search-predict__intent-arrow" aria-hidden="true">&rarr;</span>
      </a>`;
  }

  /* -----------------------------------------------------------------------
     3. Predictive Search
     ----------------------------------------------------------------------- */

  function fetchPredictiveResults(query) {
    if (abortController) abortController.abort();
    abortController = new AbortController();

    const types = [];
    const settings = window.theme || {};
    if (settings.searchShowProducts !== false) types.push('product');
    if (settings.searchShowPages !== false) types.push('page');
    if (settings.searchShowArticles !== false) types.push('article');
    // Free data the API already returns for this store: query completions
    // (rendered on top) and matching collections (their own group).
    types.push('query', 'collection');

    /* limit_scope=each: the limit applies per type. 6 product rows on
       phones, 8 on larger screens (B3-01). */
    const limit = window.matchMedia('(min-width: 768px)').matches ? 8 : 6;
    const typesParam = types.length ? `&resources[type]=${types.join(',')}` : '';
    const url = `/search/suggest.json?q=${encodeURIComponent(query)}&resources[limit]=${limit}&resources[limit_scope]=each${typesParam}`;

    // Show loading spinner
    if (searchResults) {
      searchResults.innerHTML = '<div class="tc-search-predict__loading"><span class="tc-search-predict__spinner"></span></div>';
    }

    fetch(url, { signal: abortController.signal })
      .then((res) => res.json())
      .then((data) => {
        if (!isOpen()) return;
        renderResults(data, query);
      })
      .catch((err) => {
        if (err && err.name === 'AbortError') return;
        // Fetch failed — replace the stuck spinner with a route to the
        // full search page rather than leaving a dead loading state.
        if (searchResults) {
          searchResults.innerHTML = `
            <div class="tc-search-predict__view-all">
              <a href="/search?q=${encodeURIComponent(query)}" class="tc-btn-tertiary">Press Enter to see all results</a>
            </div>`;
        }
      });
  }

  /* Caps per group (B3-01): queries ≤3, collections ≤2, pages and articles
     ≤2 between them, so products are never pushed off a phone screen. */
  const MAX_QUERIES = 3;
  const MAX_COLLECTIONS = 2;
  const MAX_CONTENT = 2;

  /* 48px slot, so 100px covers 2x screens. The CDN's width param works on
     every URL shape; the old `_100x` filename rewrite missed the ?v= suffix. */
  function thumbUrl(src) {
    if (!src) return '';
    try {
      const u = new URL(src, window.location.origin);
      u.searchParams.set('width', '100');
      return u.href;
    } catch (e) {
      return '';
    }
  }

  function toPence(value) {
    const n = parseFloat(value);
    return isNaN(n) ? 0 : Math.round(n * 100);
  }

  /* Price line. Honesty rules:
     - several sizes at different prices: "From £x", never struck through
       (pairing the smallest size's price with the largest size's RRP
       overstated the saving);
     - one price: the price only, never struck through;
     - bundles/sets: price only;
     - sold out: plain price + outlined badge (no strike). */
  function priceLine(product, strings) {
    const min = toPence(product.price_min || product.price);
    const max = toPence(product.price_max || product.price);
    if (!min) return '';

    const isRange = max > min;
    const priceText = isRange ? `From ${formatMoney(min)}` : formatMoney(min);

    if (product.available === false) {
      const soldOut = strings.soldOut || 'Sold out';
      return `<span class="tc-search-predict__price tc-search-predict__price--soldout">${escapeHTML(priceText)}</span>
        <span class="tc-search-predict__badge tc-search-predict__badge--soldout">${escapeHTML(soldOut)}</span>`;
    }

    // PM rule + owner amendment (13 Oct 2026): a single product never shows a
    // strike or "RRP", and sets show their price only here, so no compare-at
    // is printed in search at all.

    return `<span class="tc-search-predict__price">${escapeHTML(priceText)}</span>`;
  }

  function linkGroup(title, items) {
    let html = `<div class="tc-search-predict__group">
        <h3 class="tc-search-predict__group-title">${escapeHTML(title)}</h3>
        <ul class="tc-search-predict__list">`;
    items.forEach((item) => {
      html += `
          <li class="tc-search-predict__item">
            <a href="${escapeAttr(item.url)}" class="tc-search-predict__link">
              <span class="tc-search-predict__name">${titleHtml(item.title)}</span>
            </a>
          </li>`;
    });
    return html + `</ul></div>`;
  }

  function pickContent(pages, articles) {
    // One of each when both exist; otherwise two of whichever there is.
    if (pages.length && articles.length) {
      return { pages: pages.slice(0, 1), articles: articles.slice(0, MAX_CONTENT - 1) };
    }
    return { pages: pages.slice(0, MAX_CONTENT), articles: articles.slice(0, MAX_CONTENT) };
  }

  function renderResults(data, query) {
    if (!searchResults) return;

    const resources = data?.resources?.results || {};
    const products = resources.products || [];
    const content = pickContent(resources.pages || [], resources.articles || []);
    const pages = content.pages;
    const articles = content.articles;
    const typed = normalise(query);
    const queries = (resources.queries || []).filter((q) => {
      const t = normalise(q.text);
      return t && t !== typed && !/^\d/.test(t);
    }).slice(0, MAX_QUERIES);
    const collections = (resources.collections || []).slice(0, MAX_COLLECTIONS);
    const strings = (window.theme && window.theme.strings) || {};
    const intent = intentRow(query, collections.length > 0);

    if (!products.length && !pages.length && !articles.length && !collections.length) {
      // The locale string arrives HTML-escaped from Liquid; only the query needs escaping.
      const noResultsMsg = strings.searchNoResults
        ? strings.searchNoResults.replace('__TERMS__', escapeHTML(query))
        : `No results for &ldquo;${escapeHTML(query)}&rdquo;`;
      const phone = salonPhone();
      searchResults.innerHTML = `
        ${intent}
        <div class="tc-search-predict__empty">
          <p>${noResultsMsg}</p>
        </div>
      `;
      // Not a dead end: the popular routes, then the salon's phone number.
      renderDefaultState(true);
      if (phone) {
        searchResults.insertAdjacentHTML('beforeend', `
          <p class="tc-search-predict__help">Can’t find it? Call the salon on
            <a href="tel:${escapeAttr(phone.link)}">${escapeHTML(phone.label)}</a></p>`);
      }
      setStatus(`No results for ${query}`);
      return;
    }

    let html = '';

    // Query completions on top — tapping one re-runs the search with that term
    if (queries.length) {
      html += `<div class="tc-search-predict__completions">`;
      queries.forEach((q) => {
        html += `<button type="button" class="tc-search-predict__completion" data-search-completion="${escapeAttr(q.text)}">${escapeHTML(q.text)}</button>`;
      });
      html += `</div>`;
    }

    html += intent;

    // Matching collections next: a curated page is the best answer to a
    // category-style query ("shampoo", "tea tree", "gift sets").
    if (collections.length) {
      html += linkGroup('Collections', collections);
    }

    if (products.length) {
      const groupTitle = strings.searchProducts || 'Products';
      html += `<div class="tc-search-predict__group">
        <h3 class="tc-search-predict__group-title">${escapeHTML(groupTitle)}</h3>
        <ul class="tc-search-predict__list">`;

      products.forEach((product) => {
        const src = product.image || (product.featured_image && product.featured_image.url) || '';
        const imgUrl = thumbUrl(src);
        // Decorative: the product name sits right beside it in the same link.
        // Eager: 6–8 tiny thumbs inside a scroll box — lazy left visible rows
        // blank until the shopper scrolled the list.
        const img = imgUrl
          ? `<img src="${escapeAttr(imgUrl)}" alt="" width="48" height="48" decoding="async">`
          : '';

        html += `
          <li class="tc-search-predict__item">
            <a href="${escapeAttr(product.url)}" class="tc-search-predict__link">
              <span class="tc-search-predict__img">${img}</span>
              <span class="tc-search-predict__info">
                <span class="tc-search-predict__name">${titleHtml(product.title)}</span>
                <span class="tc-search-predict__price-wrap">${priceLine(product, strings)}</span>
              </span>
            </a>
          </li>`;
      });

      html += `</ul></div>`;
    }

    if (articles.length) {
      html += linkGroup(strings.searchArticles || 'Hair advice', articles);
    }

    if (pages.length) {
      html += linkGroup(strings.searchPages || 'Pages', pages);
    }

    // View all link
    html += `
      <div class="tc-search-predict__view-all">
        <a href="/search?q=${encodeURIComponent(query)}" class="tc-btn-tertiary">View all results</a>
      </div>`;

    searchResults.innerHTML = html;
    searchResults.scrollTop = 0;

    const parts = [];
    if (products.length) parts.push(plural(products.length, 'product', 'products'));
    if (collections.length) parts.push(plural(collections.length, 'collection', 'collections'));
    if (articles.length + pages.length) parts.push(plural(articles.length + pages.length, 'page', 'pages'));
    setStatus(`${parts.join(', ')} for ${query}. Use the arrow keys to move through them.`);
  }

  /* -----------------------------------------------------------------------
     4. Input Handler
     ----------------------------------------------------------------------- */

  searchInput?.addEventListener('input', () => {
    const query = searchInput.value.trim();

    clearTimeout(debounceTimer);

    if (query.length < MIN_CHARS) {
      // Drop any in-flight request so a late response can't overwrite the default panel
      if (abortController) abortController.abort();
      renderDefaultState();
      if (searchStatus) searchStatus.textContent = '';
      return;
    }

    debounceTimer = setTimeout(() => {
      fetchPredictiveResults(query);
    }, DEBOUNCE_MS);
  });

  // Tapping a completion re-runs the search with the suggested term
  searchResults?.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-search-completion]');
    if (!btn || !searchInput) return;
    searchInput.value = btn.getAttribute('data-search-completion');
    searchInput.focus();
    fetchPredictiveResults(searchInput.value);
  });

  /* Empty-overlay default (nav IA S1): two rows of routes instead of a blank
     panel. Handles are the IA's menu targets; labels match the menu. */
  const POPULAR_LINKS = [
    ['Best Sellers', '/collections/top-selling-products-purchased-by-our-regulars'],
    ['Bundles & Sets', '/collections/duos'],
    ['Gift Sets', '/collections/gift-sets-duos'],
    ['Freeze & Shine', '/products/pm-freeze-and-shine-super-spray'],
    ['1 Litre', '/collections/paul-mitchell-1-litre'],
    ['Travel', '/collections/travel-size'],
    ['Tea Tree', '/collections/tea-tree'],
    ['Tools', '/collections/tools'],
  ];

  const POPULAR_SEARCHES = [
    'freeze and shine 1000ml',
    'tea tree shampoo',
    'super skinny serum',
    'the detangler',
    'purple shampoo',
  ];

  function chipRow(title, links) {
    const chips = links.map(([label, href]) =>
      `<a class="tc-search-predict__chip" href="${escapeAttr(href)}">${escapeHTML(label)}</a>`
    ).join('');
    return `
      <div class="tc-search-predict__default">
        <h3 class="tc-search-predict__group-title">${escapeHTML(title)}</h3>
        <div class="tc-search-predict__chips">${chips}</div>
      </div>`;
  }

  function renderDefaultState(append) {
    if (!searchResults) return;
    const html = chipRow('Popular', POPULAR_LINKS) +
      chipRow('Popular searches', POPULAR_SEARCHES.map((term) => [term, `/search?q=${encodeURIComponent(term)}`]));
    if (append) searchResults.insertAdjacentHTML('beforeend', html);
    else searchResults.innerHTML = html;
  }

  /* -----------------------------------------------------------------------
     5. Utilities
     ----------------------------------------------------------------------- */

  function escapeHTML(str) {
    return window.TheColor.utils.escapeHtml(str);
  }

  function escapeAttr(str) {
    return window.TheColor.utils.escapeAttr(str);
  }

  function formatMoney(cents) {
    return window.TheColor.utils.formatMoney(cents);
  }
})();
